/*
 * Copyright (c) 2018, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */
import {
  ChakraProvider,
  Container,
  Table,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { CustomUserClaim, UserClaims } from "@okta/okta-auth-js";
import { useOktaAuth } from "@okta/okta-react";
import { useEffect, useState } from "react";

export const Profile = () => {
  const { authState, oktaAuth } = useOktaAuth();
  const [userInfo, setUserInfo] = useState<UserClaims | null>(null);

  useEffect(() => {
    if (!authState || !authState.isAuthenticated) {
      // When user isn't authenticated, forget any user info
      setUserInfo(null);
    } else {
      oktaAuth
        .getUser()
        .then((info) => {
          setUserInfo(info);
        })
        .catch(() => {
          throw new Error("Get User Profile Error");
        });
    }
  }, [authState, oktaAuth]);

  const convertClaimValue = (
    claimValue: CustomUserClaim | CustomUserClaim[]
  ) => {
    switch (typeof claimValue) {
      case "string":
        return claimValue;
      case "number":
        return claimValue.toString();
      case "boolean":
        return claimValue.toString();
      default:
        return "Uknown";
    }
  };

  const ProfileInfo = () => {
    return (
      <Container>
        <ChakraProvider>
          <Table>
            <Thead>
              <Th>Claim</Th>
              <Th>Value</Th>{" "}
              {userInfo &&
                Object.entries(userInfo).map(
                  (
                    claimEntry: [string, CustomUserClaim | CustomUserClaim[]]
                  ) => {
                    const claimName: string = claimEntry[0];
                    const claimValue: CustomUserClaim | CustomUserClaim[] =
                      claimEntry[1];
                    const claimId = `claim-${claimName}`;
                    return (
                      <Tr key={claimName}>
                        <Td>{claimName}</Td>
                        <Td id={claimId}>{convertClaimValue(claimValue)}</Td>
                      </Tr>
                    );
                  }
                )}
            </Thead>
          </Table>
        </ChakraProvider>
      </Container>
    );
  };

  if (!userInfo) {
    return (
      <div>
        <p>Fetching user profile...</p>
      </div>
    );
  }

  return <ProfileInfo />;
};

export default Profile;
